import React, { useCallback, useEffect, useRef, useState } from 'react'
import { formatDateWithTime } from 'formatters'
import { makeStyles } from '@material-ui/core/styles'
import { Button, Loader, Table, Typography } from 'foundations-library/components'
import { IEnrollmentEvent, IEnrollmentEventCollection, EnrollmentEventsContext } from '../contexts/EnrollmentEventsContext'

const useStyles = makeStyles((theme) => ({
    mb15: {
        marginBottom: 15
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center'
    }
}))

const DEFAULT_VISIBLE_ROWS = 10
const SCROLL_BUFFER_PX = 24

const getSeeMoreButtonLabel = (seeMore: boolean, loading: boolean) => {
    if (loading) {
        return 'Loading...'
    }

    return seeMore ? 'See less' : 'See more'
}

type PropsType = {
    entityType: 'brand' | 'campaign',
    entityId: string,
    viewHeight: number,
    breakPoints: {
        belowXS: boolean,
        belowSM: boolean
    }
}

const getTableRowData = (item) => [formatDateWithTime(item.createdAt), item.eventName, item.description]

/**
 *
 */
const EnrollmentEvents = (props: PropsType): JSX.Element => {
    const classess = useStyles()
    const [seeMore, setSeeMore] = useState(false)
    const eventsContext = React.useContext(EnrollmentEventsContext)
    const tableWrapperRef = useRef<HTMLDivElement | null>(null)
    const listEventsRef = useRef(eventsContext.list)
    const requestVersionRef = useRef(0)
    const isFetchingRef = useRef(false)
    const [eventList, setEventList] = useState([] as IEnrollmentEvent[])
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        listEventsRef.current = eventsContext.list
    }, [eventsContext.list])

    const list = useCallback(async (requestVersion = requestVersionRef.current) => {
        if (isFetchingRef.current) return null

        isFetchingRef.current = true
        setLoading(true)
        try {
            const res = (await listEventsRef.current(props.entityType, props.entityId)) as IEnrollmentEventCollection

            if (requestVersion !== requestVersionRef.current) return null

            setEventList(res.events)
            setHasMore(res.hasMore)

            return res
        } finally {
            if (requestVersion === requestVersionRef.current) {
                isFetchingRef.current = false
                setLoading(false)
            }
        }
    }, [props.entityId, props.entityType])

    useEffect(() => {
        requestVersionRef.current += 1
        isFetchingRef.current = false
        setSeeMore(false)
        setEventList([])
        setHasMore(false)
        void list(requestVersionRef.current)
    }, [list, props.entityId, props.entityType])

    useEffect(() => {
        if (!seeMore || !hasMore || eventList.length > DEFAULT_VISIBLE_ROWS || isFetchingRef.current) return

        void list()
    }, [eventList.length, hasMore, list, seeMore])

    useEffect(() => {
        if (!seeMore) return

        const tableContainer = tableWrapperRef.current?.querySelector('.table-container') as HTMLDivElement | null
        if (!tableContainer) return

        const onScroll = async () => {
            const remainingScroll = tableContainer.scrollHeight - tableContainer.scrollTop - tableContainer.clientHeight

            if (remainingScroll > SCROLL_BUFFER_PX || isFetchingRef.current) return

            if (!hasMore) return

            await list()
        }

        tableContainer.addEventListener('scroll', onScroll)

        return () => {
            tableContainer.removeEventListener('scroll', onScroll)
        }
    }, [hasMore, list, seeMore])

    const rows = seeMore
        ? eventList.map((item) => getTableRowData(item))
        : eventList.slice(0, 1).map((item) => getTableRowData(item))

    if (loading && !eventList.length) return <Loader inlineBlock />
    return <div data-testid='enrollment-events'>
        {
            rows.length
                ? <div ref={tableWrapperRef}>
                    <Table
                        columns={['Date', 'Event', 'Description']}
                        rows={rows}
                        className={classess.mb15}
                        scrollAfterRow={seeMore ? DEFAULT_VISIBLE_ROWS : undefined}
                        data-testid='events-table'
                    />
                </div>
                : <Typography
                    variant='subtitle2'
                    align='center'
                    data-testid='enrollment-events-no-items'
                >No events found.</Typography>
        }
        {
            eventList.length > 1
                ? <div className={classess.buttonContainer}>
                    <Button
                        variant='outlined'
                        onClick={() => setSeeMore((prev) => !prev)}
                        data-testid='enrollment-events-see-more-btn'
                    >
                        {getSeeMoreButtonLabel(seeMore, loading)}
                    </Button>
                </div>
                : null

        }
    </div>
}

/**
 *
 */
export default EnrollmentEvents
